import type {
  ApprovalGroupFormData,
  ApprovalUser,
  SeniorityValues,
} from "@/types/entities/approval-groups";
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import type { ReactNode } from "react";

interface GroupFormContextType {
  // Form data
  formData: ApprovalGroupFormData;
  setFormData: (data: Partial<ApprovalGroupFormData>) => void;

  // Users data
  selectedUsers: ApprovalUser[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<ApprovalUser[]>>;

  // Form validation state
  isFormValid: boolean;
  setIsFormValid: (valid: boolean) => void;

  // Loading state
  isSubmitting: boolean;
  setIsSubmitting: (submitting: boolean) => void;

  // function for saving
  onSave: (
    data: ApprovalGroupFormData,
    users: ApprovalUser[],
    seniorityValues: SeniorityValues,
  ) => Promise<void>;

  // Error handling
  apiErrors: Record<string, string>;
  clearApiErrors: () => void;

  // Validation helpers
  canSave: boolean;
  allUsersHaveLevel: boolean;
  leaderAlreadySelected: boolean;
  supervisorAlreadySelected: boolean;
}

const GroupFormContext = createContext<GroupFormContextType | undefined>(
  undefined,
);

interface GroupFormProviderProps {
  children: ReactNode;
  initValues?: {
    formData: ApprovalGroupFormData;
    selectedUsers: ApprovalUser[];
  };
  // onSave handling
  onSave: (
    data: ApprovalGroupFormData,
    users: ApprovalUser[],
    seniorityValues: SeniorityValues,
  ) => Promise<void>;
  //error handling
  apiErrors: Record<string, string>;
  clearApiErrors: () => void;
}

export const GroupFormProvider = ({
  initValues,
  children,
  onSave,
  apiErrors,
  clearApiErrors,
}: GroupFormProviderProps) => {
  const [formData, setFormDataState] = useState<ApprovalGroupFormData>(
    initValues?.formData || {
      name: "",
      description: "",
      type: "",
    },
  );

  const [selectedUsers, setSelectedUsers] = useState<ApprovalUser[]>(
    initValues?.selectedUsers || [],
  );
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setFormData = useCallback((data: Partial<ApprovalGroupFormData>) => {
    setFormDataState((prev) => ({ ...prev, ...data }));
  }, []);

  const allUsersHaveLevel = useMemo(
    () => selectedUsers.every((user) => user.level !== undefined),
    [selectedUsers],
  );

  const leaderAlreadySelected = useMemo(
    () => selectedUsers.some((user) => user.level && user.level === "lider"),
    [selectedUsers],
  );

  const supervisorAlreadySelected = useMemo(
    () => selectedUsers.some((user) => user.level && user.level === "senior"),
    [selectedUsers],
  );

  const canSave = useMemo(
    () =>
      isFormValid &&
      !isSubmitting &&
      (selectedUsers.length === 0 || allUsersHaveLevel) &&
      leaderAlreadySelected,
    [
      isFormValid,
      isSubmitting,
      selectedUsers.length,
      allUsersHaveLevel,
      leaderAlreadySelected,
    ],
  );

  const value = useMemo(
    (): GroupFormContextType => ({
      formData,
      setFormData,
      selectedUsers,
      setSelectedUsers,
      isFormValid,
      setIsFormValid,
      isSubmitting,
      setIsSubmitting,
      onSave,
      canSave,
      allUsersHaveLevel,
      leaderAlreadySelected,
      apiErrors,
      supervisorAlreadySelected,
      clearApiErrors,
    }),
    [
      formData,
      setFormData,
      selectedUsers,
      setSelectedUsers,
      isFormValid,
      setIsFormValid,
      isSubmitting,
      setIsSubmitting,
      onSave,
      canSave,
      allUsersHaveLevel,
      leaderAlreadySelected,
      supervisorAlreadySelected,
      apiErrors,
      clearApiErrors,
    ],
  );

  return (
    <GroupFormContext.Provider value={value}>
      {children}
    </GroupFormContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useApprovalGroupForm = () => {
  const context = useContext(GroupFormContext);
  if (context === undefined) {
    throw new Error(
      "useApprovalGroupForm must be used within a GroupFormProvider",
    );
  }
  return context;
};
